<!-- src/views/admin/engineering/AdminProjectEditorView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] p-4 sm:p-6 md:p-8">
    <div class="max-w-7xl mx-auto">
      
      <!-- Header -->
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-gray-800">{{ isEditing ? 'Edit Project' : 'Create New Project' }}</h1>
        <button @click="$router.go(-1)" class="text-sm text-brand-primary hover:underline">
          &larr; Cancel & Back
        </button>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-20">
        <p class="text-gray-500">Loading...</p>
      </div>

      <form v-else @submit.prevent="saveProject" class="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        
        <!-- Image Banner / Upload -->
        <div class="relative bg-gray-100 h-64 flex items-center justify-center border-b border-gray-200 group">
          <img v-if="previewImage" :src="previewImage" class="w-full h-full object-cover transition-opacity duration-300" :class="{ 'opacity-50': isHoveringImage }" alt="Project Preview" />
          
          <div v-else class="text-gray-400 flex flex-col items-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span class="text-sm font-medium">Upload Project Cover Photo</span>
          </div>
          
          <!-- Upload Button Overlay -->
          <label class="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity duration-200">
            <span class="bg-white text-gray-800 px-4 py-2 rounded-md font-medium shadow-sm hover:bg-gray-50">
              {{ previewImage ? 'Change Photo' : 'Select Photo' }}
            </span>
            <input type="file" class="hidden" accept="image/*" @change="handleImageUpload">
          </label>
        </div>

        <div class="p-6 sm:p-8 space-y-8">
          
          <!-- Section 1: Core Information -->
          <section>
            <h2 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Project Overview</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div class="md:col-span-2">
                <label class="form-label">Project Name <span class="text-red-500">*</span></label>
                <input v-model="project.name" type="text" required class="form-input text-lg font-medium" placeholder="e.g. Manea Year 4" />
              </div>

              <div>
                <label class="form-label">Status <span class="text-red-500">*</span></label>
                <select v-model="project.status" class="form-input">
                  <option value="In Planning">In Planning</option>
                  <option value="Active">Active</option>
                  <option value="Complete">Complete</option>
                  <option value="On Hold">On Hold</option>
                </select>
              </div>

              <!-- Location Group -->
              <div class="md:col-span-2 bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <h3 class="text-sm font-bold text-gray-700 mb-3 border-b border-gray-200 pb-2">Location Details</h3>
                  
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                      <!-- Site Address -->
                      <div>
                        <div class="flex items-end mb-1 min-h-[24px]">
                            <label class="form-label mb-0">Site Address</label>
                        </div>
                        <input v-model="project.siteAddress" type="text" class="form-input" placeholder="e.g. Poplar Depot, London" />
                      </div>

                      <!-- What3Words -->
                      <div>
                        <div class="flex justify-between items-end mb-1 min-h-[24px]">
                            <label class="form-label mb-0">What3Words</label>
                            <a href="https://what3words.com/" target="_blank" rel="noopener noreferrer" class="opacity-80 hover:opacity-100 transition-opacity" title="Find location on What3Words">
                                 <img src="/images/what3words-logo.png" alt="W3W" class="h-6 w-auto">
                            </a>
                        </div>
                        <input v-model="project.siteWhat3Words" type="text" class="form-input" placeholder="///word.word.word" />
                      </div>
                  </div>
                  <p class="text-xs text-gray-500 mt-2">Please provide at least one location identifier.</p>
              </div>

            </div>
          </section>

          <!-- Section 2: Schedule & Technical -->
          <section>
            <h2 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Schedule & Technical Details</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="form-label">Project Start Date</label>
                <input v-model="project.startDate" type="date" class="form-input" />
              </div>
              <div>
                <label class="form-label">Project End Date</label>
                <input v-model="project.endDate" type="date" class="form-input" :min="project.startDate" />
              </div>
              <div>
                <label class="form-label">Bridge Reference</label>
                <input v-model="project.bridgeRef" type="text" class="form-input" placeholder="e.g. 1813A, 1814" />
              </div>
              <div>
                <label class="form-label">Line / Track</label>
                <input v-model="project.lineRef" type="text" class="form-input" placeholder="e.g. Down Main / Both" />
              </div>
            </div>
          </section>

          <!-- Section 3: Scope -->
          <section>
            <h2 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Scope of Works / Notes</h2>
            <textarea v-model="project.description" rows="5" class="form-input" placeholder="Enter general project notes, scope details, or snagging items here..."></textarea>
          </section>

          <!-- Section 4: Document Uploads -->
          <section>
            <h2 class="text-lg font-semibold text-gray-800 border-b pb-2 mb-4">Project Documents</h2>
            
            <!-- List Existing Documents -->
            <div v-if="project.documents && project.documents.length > 0" class="mb-4 space-y-2">
                <div v-for="(doc, index) in project.documents" :key="index" class="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100">
                    <div class="flex items-center overflow-hidden">
                         <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <a :href="doc.url" target="_blank" class="text-sm text-brand-primary hover:underline truncate">{{ doc.name }}</a>
                    </div>
                    <span class="text-xs text-gray-400">Saved</span>
                </div>
            </div>

            <!-- Upload New -->
            <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer relative">
                <input type="file" multiple class="absolute inset-0 opacity-0 cursor-pointer" @change="handleDocumentUpload">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p class="text-sm text-gray-600">Click to upload documents (PDF, DOCX, etc.)</p>
                <p class="text-xs text-gray-400 mt-1">You can select multiple files</p>
            </div>
            
            <!-- Staged Files Preview -->
            <div v-if="newDocuments.length > 0" class="mt-4 space-y-2">
                <p class="text-xs font-semibold text-gray-500 uppercase">Ready to Upload:</p>
                <div v-for="(file, index) in newDocuments" :key="index" class="flex items-center justify-between p-2 bg-blue-50 rounded-md border border-blue-100">
                    <span class="text-sm text-blue-700 truncate">{{ file.name }}</span>
                    <button type="button" @click="removeStagedDocument(index)" class="text-red-500 hover:text-red-700">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
          </section>

        </div>

        <!-- Footer Actions -->
        <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <button v-if="isEditing" type="button" @click="deleteProject" class="text-red-600 hover:text-red-800 text-sm font-medium">
            Delete Project
          </button>
          <div v-else></div>
          
          <div class="flex space-x-3">
            <button type="button" @click="$router.go(-1)" class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 font-medium">
              Cancel
            </button>
            <button type="submit" :disabled="isSaving" class="px-6 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-accent shadow-md font-medium disabled:opacity-50 flex items-center">
              <svg v-if="isSaving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              {{ isSaving ? 'Saving...' : 'Save Project' }}
            </button>
          </div>
        </div>

      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { db, firebaseApp } from '@/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');

const projectId = route.params.id === 'new' ? null : route.params.id;
const isEditing = computed(() => !!projectId);
const isSaving = ref(false);
const isLoading = ref(true);
const isHoveringImage = ref(false);

// Project Data Model
const project = reactive({
  name: '',
  status: 'In Planning',
  location: '', // Legacy support
  siteAddress: '',
  siteWhat3Words: '',
  startDate: '',
  endDate: '',
  bridgeRef: '',
  lineRef: '',
  description: '',
  imageUrl: '',
  documents: []
});

const previewImage = ref('');
const imageFile = ref(null);
const newDocuments = ref([]);

// ---------------------------------------------------------
// HELPER FUNCTIONS (Must be defined BEFORE they are called)
// ---------------------------------------------------------

const fetchProject = async () => {
  try {
    const docRef = doc(db, 'clients', userStore.clientId, 'projects', projectId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      const data = snap.data();
      
      // Legacy support
      if (data.location && !data.siteAddress) {
          data.siteAddress = data.location;
      }
      
      Object.assign(project, data);
      previewImage.value = data.imageUrl;
    } else {
        alert("Project not found.");
        router.push({ name: 'EngineeringProjects' });
    }
  } catch (e) { 
      console.error("Error loading project", e); 
  }
};

const initPage = async () => {
  if (userStore.isUserProfileLoading) return;

  const isAdmin = userStore.isAdmin; 
  const permissions = userStore.permissions || {};
  const isEngineeringManager = permissions.canViewEngineering;

  if (!isAdmin && !isEngineeringManager) {
    alert("Access Denied. You do not have permission to create or edit projects.");
    router.push({ name: 'EngineeringProjects' });
    return;
  }

  if (userStore.clientId && isEditing.value) {
    // Now fetchProject is defined before use
    await fetchProject();
  }
  
  isLoading.value = false;
};

const handleImageUpload = (event) => {
  const file = event.target.files[0];
  if (file) {
    imageFile.value = file;
    previewImage.value = URL.createObjectURL(file);
  }
};

const handleDocumentUpload = (event) => {
    const files = Array.from(event.target.files);
    newDocuments.value.push(...files);
    event.target.value = '';
};

const removeStagedDocument = (index) => {
    newDocuments.value.splice(index, 1);
};

const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result;
        const base64 = result.split(',')[1];
        resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const saveProject = async () => {
  isSaving.value = true;
  try {
    let imageBase64 = null;
    if (imageFile.value) {
        imageBase64 = await readFileAsBase64(imageFile.value);
    }

    const documentsPayload = [];
    if (newDocuments.value.length > 0) {
        for (const file of newDocuments.value) {
            const b64 = await readFileAsBase64(file);
            documentsPayload.push({
                name: file.name,
                type: file.type,
                base64: b64
            });
        }
    }

    const manageProject = httpsCallable(functions, 'manageProject');
    const result = await manageProject({
        action: 'save',
        projectId: projectId,
        projectData: { 
            name: project.name,
            status: project.status,
            siteAddress: project.siteAddress,
            siteWhat3Words: project.siteWhat3Words,
            location: project.siteAddress, // Sync legacy field
            startDate: project.startDate,
            endDate: project.endDate,
            bridgeRef: project.bridgeRef,
            lineRef: project.lineRef,
            description: project.description
        },
        imageBase64: imageBase64,
        documents: documentsPayload
    });

    if (result.data.success) {
        router.push({ name: 'AdminEngineeringDashboard' });
    } else {
        throw new Error('Unknown error occurred');
    }

  } catch (e) {
    console.error("Error saving project", e);
    alert(`Failed to save project. ${e.message}`);
  } finally {
    isSaving.value = false;
  }
};

const deleteProject = async () => {
  if (!confirm("Are you sure you want to delete this project?")) return;
  try {
    const manageProject = httpsCallable(functions, 'manageProject');
    await manageProject({
        action: 'delete',
        projectId: projectId
    });
    router.push({ name: 'AdminEngineeringDashboard' });
  } catch (e) { alert("Failed to delete project."); }
};

// ---------------------------------------------------------
// LIFECYCLE (Watches/Mounted must be AFTER functions are defined)
// ---------------------------------------------------------

watch(
  () => userStore.isUserProfileLoading,
  (isLoading) => {
    if (!isLoading) {
      initPage();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (!userStore.isUserProfileLoading && userStore.isAuthReady) {
    initPage();
  }
});
</script>

<style scoped>
.form-label { @apply block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1; }
.form-input { @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-brand-primary focus:border-brand-primary sm:text-sm; }
</style>